import React, { useState, useEffect, useCallback } from "react";
import Loader from "./custom/Loader";
import { IoMdClose } from "react-icons/io";
import { integrationData } from "../utils/constants";
import { authenticateApp } from "../utils/auth"; // Your auth.js
import { getAuthenticatedApps, unauthApp } from "../services/api";
import { FaCheck, FaPen } from "react-icons/fa6";
import { MdOutlineAdd } from "react-icons/md";
import ResourceModal from "./modals/ResourceModal";
import { useEmails } from "../context/EmailsProvider";
import { DataState } from "../context/DataProvider";
import { useTeams } from "../context/TeamsProvider";

// NEW: Import the Modal and the MCP Manager components and an icon
import Modal from "./base/Modal";
import McpConnectionManager from "./McpConnections/McpConnectionManager";
import { useMCP } from "../context/McpProvider";
import WorkEmailCapture from "./premiumFeatureComponent/WorkEmailCapture";
import JiraResourceModal from "./modals/JiraResourceModal";
import ConfluenceResourceModal from "./modals/ConfluenceResourceModal";
import TeamsConfigurationModal from "./modals/TeamsConfigurationModal";
import GohighlevelResourceModal from "./modals/GohighlevelResourceModal";
import QuickBooksEnvModal from "./modals/QuickBooksEnvModal";
import ShiprocketAuthModal from "./modals/ShiprocketAuthModal";
import RazorpayAuthModal from "./modals/RazorpayAuthModal";

function AddIntegration({ openMcpModal }) {
  return (
    <div className="flex items-center justify-between w-full p-6 mt-2">
      <h2 className="font-semibold text-gray-200 text-md">
        Integrate Apps through MCP
      </h2>

      {/* Button Container */}
      <div className="flex space-x-4">
        <button
          onClick={openMcpModal}
          className="px-6 py-2 text-sm font-medium text-white transition duration-200 border rounded-md bg-zinc-900 hover:bg-zinc-800 border-zinc-600"
        >
          MCP Connection
        </button>
      </div>
    </div>
  );
}

const Integration = ({ onClose, user_google_id, bot_id, onlyEmailApps = false, isTeamsInitialized }) => {

  const [loading, setLoading] = useState(false);
  const [integratedApps, setIntegratedApps] = useState([]);
  const [notIntegratedApps, setNotIntegratedApps] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAppName, setModalAppName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPremium, setShowPremium] = useState(false);
  const { refreshConnections, globalConnections, loadConnections, setCheckResult } = useMCP();
  const [showNotIntegratedApps, setShowNotIntegratedApps] = useState(false);
  const [activeTab, setActiveTab] = useState("Apps");

  const [appsClientConfig, setAppsClientConfig] = useState({}); // {appname : {client_id, client_secret, redirect_uri}}


  // NEW: State for controlling the MCP Connection Manager modal
  const [isMcpModalOpen, setIsMcpModalOpen] = useState(false);

  // State for controlling the QuickBooks Environment Selection modal
  const [isQuickBooksEnvModalOpen, setIsQuickBooksEnvModalOpen] = useState(false);

  const { userInfo, userGoogleId: accessToken } = DataState();
  const { isTeamsInitialized: isTeamsInitFromDataState } = useTeams();
  const {
    setGmailAuthStatus,
    setOutlookAuthStatus,
  } = useEmails();

  // Use the prop if passed, otherwise fallback to context or assume false
  const inTeams = typeof isTeamsInitialized === 'boolean' ? isTeamsInitialized : isTeamsInitFromDataState || false;


  //preparing apps_data based on onlyEmailApps flag
  let apps_data = [...integrationData];

  if (onlyEmailApps) {
    apps_data = apps_data.filter(app =>
      ["gmail", "microsoft_outlook"].includes(app.value)
    );
  } else {
    apps_data = apps_data.map((app, unique_index) => {
      app.unique_index = unique_index;
      return app;
    })
  }

  const fetchIntegratedApps = async (selectedAppForIntegration = null) => {
    setLoading(true);
    try {
      const result = await getAuthenticatedApps(user_google_id, bot_id);
      const integrated = result.apps;

      // here I want to insert the redirect_url from the result into each app in apps client config state
      const updatedAppsClientConfig = { ...(result.apps_client_config || {}) };
      integrated.forEach((app) => {
        if (result.apps_client_config[app]) {
          updatedAppsClientConfig[app] = {
            client_id: result.apps_client_config[app].client_id || "",
            client_secret: result.apps_client_config[app].client_secret || "",
            use_user_client: result.apps_client_config[app].use_user_client || false,
            redirect_uri: result.redirect_uri || "",
          };
        }
      });
      setAppsClientConfig(updatedAppsClientConfig);
      console.log("Apps Client Config:", updatedAppsClientConfig);

      const integratedAppsFiltered = apps_data.filter(
        (app) => integrated.includes(app.value)
      );
      const notIntegratedAppsFiltered = apps_data.filter(
        (app) => !integrated.includes(app.value)
      );
      setIntegratedApps(integratedAppsFiltered);
      setNotIntegratedApps(notIntegratedAppsFiltered);

    } catch (error) {
      console.error(error);
    } finally {
      refreshConnections();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegratedApps();
  }, [user_google_id, bot_id]);

  // Global keydown handler for building the search term
  const handleKeyDown = useCallback(
    (e) => {
      // NEW: Disable search listener if any modal or premium view is open
      if (isModalOpen || isMcpModalOpen || showPremium) return;

      // Ignore if any modifier keys are pressed
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      // if (e.key === "Backspace") {
      //   setSearchTerm((prev) => prev.slice(0, -1));
      if (e.key === "Backspace" || e.key === "Delete") {
        setSearchTerm("");
      } else if (e.key && e.key.length === 1) {
        // Append all printable characters including space
        setSearchTerm((prev) => prev + e.key);
      }
    },
    [isModalOpen, isMcpModalOpen, showPremium] // Add these dependencies so the listener updates when state changes
  );
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Clear search manually (completely)
  const clearSearch = () => {
    setSearchTerm("");
  };

  // Filter apps based on a fuzzy regex match from the search term.
  const filterApps = (apps) => {
    if (!searchTerm) return apps;
    try {
      // Create a fuzzy regex pattern, e.g. "olook" => "o.*l.*o.*o.*k"
      const pattern = searchTerm.split("").join(".*");
      const regex = new RegExp(pattern, "i");
      return apps.filter((app) => regex.test(app.label));
    } catch (error) {
      console.error("Regex error:", error);
      return apps;
    }
  };

  // -------- AUTH --------
  const handleIntegrateButtonClick = async (app) => {
    const googleApps = [
      "gmail",
      "google_drive",
      "google_calendar",
      "google_docs",
      "google_sheets",
      "google_forms",
      "google_tasks",
      "google_keep",
    ];

    if (
      googleApps.includes(app.value) &&
      (!userInfo || !userInfo.enterprise_access)
    ) {
      setShowPremium(true);
      return;
    }

    if (app.value === "shiprocket") {
      setModalAppName("shiprocket");
      setIsModalOpen(true);
      return;
    }

    if (app.value === "razorpay") {
      setModalAppName("razorpay");
      setIsModalOpen(true);
      return;
    }

    // For QuickBooks, show Sandbox/Production chooser first
    if (app.value === "quickbooks") {
      setIsQuickBooksEnvModalOpen(true);
      return;
    }

    setLoading(true);

    await authenticateApp(
      app.value,
      user_google_id,
      async () => {
        await fetchIntegratedApps();
        setLoading(false);
      },
      (appName) => {
        if (onlyEmailApps && appName === "gmail") {
          setGmailAuthStatus((p) => ({ status: true, state: p.state + 1 }));
        }
        if (onlyEmailApps && appName === "microsoft_outlook") {
          setOutlookAuthStatus((p) => ({ status: true, state: p.state + 1 }));
        }
      },
      inTeams
    );
  };

  // Handler for QuickBooks environment selection
  const handleQuickBooksEnvSelect = async (envType) => {
    setIsQuickBooksEnvModalOpen(false);
    setLoading(true);
    await authenticateApp(
      "quickbooks",
      user_google_id,
      (appName) => {
        fetchIntegratedApps(appName);
        setLoading(false);
      },
      (appName) => { },
      inTeams,
      { envType } // Pass environment type option
    );
  };

  const handleUnAuthorisationButtonClick = async (selectedAppForUnauth) => {
    if (selectedAppForUnauth) {
      setLoading(true);
      const response = await unauthApp(
        selectedAppForUnauth.value,
        bot_id,
        user_google_id
      );

      if (response.error) {
        console.error("Error:", response.error);
      }
      if (onlyEmailApps) {
        if (selectedAppForUnauth.value === "gmail") {
          setGmailAuthStatus((prev) => ({
            status: false,
            state: prev.state + 1,
          }));
        } else if (selectedAppForUnauth.value === "microsoft_outlook") {
          setOutlookAuthStatus((prev) => ({
            status: false,
            state: prev.state + 1,
          }));
        }
      }
      fetchIntegratedApps();

    }
  };

  const openModal = (appName) => {
    setModalAppName(appName);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalAppName("");
  };

  // NEW: Handlers for the MCP modal
  const openMcpModal = () => setIsMcpModalOpen(true);
  const closeMcpModal = () => setIsMcpModalOpen(false);

  const filteredIntegratedApps = filterApps(integratedApps);
  const filteredNotIntegratedApps = filterApps(notIntegratedApps);

  const TABS = ["Apps", "Custom MCP"];


  return (
    <div className="backdrop-blur-sm fixed inset-0 flex items-center justify-center z-50">
      <div
        className="bg-black opacity-50 absolute inset-0"
        onClick={onClose}
      ></div>

      {showPremium ? (
        <div className="z-10 max-w-6xl w-[90%]">
          <WorkEmailCapture setShowPremium={setShowPremium} />
        </div>
      ) : (
        <div className={`relative bg-1 text-p-text h-[90%] max-w-3xl w-[90%]      rounded-2xl flex flex-col z-10 border border-zinc-700
         ${onlyEmailApps ? "h-fit" : "max-h-[70%] overflow-hidden"}
         `}>

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-700">
            <h2 className="text-xl font-semibold text-zinc-200">
              {onlyEmailApps ? "Your MailBoxes" : "Your Integrations"}
            </h2>
            <button onClick={onClose}>
              <IoMdClose className="text-zinc-400" size={20} />
            </button>
          </div>

          {/* Tabs + Search */}
          <div className="flex items-center justify-between px-6 pt-4 border-b border-zinc-700">
            <div className="flex gap-8">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`pb-3 text-sm ${activeTab === tab
                    ? "text-white border-b-2 border-white"
                    : "text-zinc-400"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="px-6 py-6 max-h-[65vh] overflow-y-auto">
            {loading ? (
              <div className={`pr-4 pl-4 flex items-center justify-center text-p-text  ${onlyEmailApps ? "" : "h-64"} `}>
                <span className="h-12">
                  <Loader />
                </span>
              </div>
            ) : (
              <>
                {/* ===================== APPS TAB ===================== */}
                {activeTab === "Apps" && (
                  <div className={`flex flex-wrap max-sm:flex-col max-sm:items-center gap-x-4 gap-y-4 ${onlyEmailApps ? "justify-center items-center pt-0" : ""}`}>
                    {filteredIntegratedApps.map((item) => (
                      <div
                        key={item.unique_index}
                        className="flex items-center gap-2 p-1 border rounded-full w-fit border-zinc-400 group"
                      >
                        <div className="p-1 rounded-full bg-zinc-200">
                          <img
                            alt={item.label}
                            src={item.image}
                            className="w-5 h-5"
                          />
                        </div>
                        <div className="ml-1 font-semibold text-center text-p-text text-md">
                          {item.label}
                        </div>
                        {(["jira", "confluence", "azure_devops", "microsoft_outlook", "github", "gmail", "microsoft_teams", "gohighlevel"].includes(item.value)) && (
                          <button
                            onClick={() => openModal(item.value)}
                            className="hidden p-2 ml-3 text-blue-700 transition-all duration-300 bg-white rounded-full hover:text-blue-800 group-hover:block"
                          >
                            <FaPen size={10} />
                          </button>
                        )}
                        <button
                          onClick={() => handleUnAuthorisationButtonClick(item)}
                          className="p-2 text-xs transition-all duration-300 bg-green-700 rounded-full hover:bg-green-800"
                        >
                          <FaCheck size={10} />
                        </button>
                      </div>
                    ))}
                    {filteredNotIntegratedApps.map((item) => (
                      <div
                        key={`Not-integrated-app-${item.unique_index}`}
                        className="flex items-center gap-2 p-1 border rounded-full w-fit border-zinc-400"
                      >
                        <div className="p-1 rounded-full bg-zinc-200">
                          <img
                            alt={item.label}
                            src={item.image}
                            className="w-5 h-5"
                          />
                        </div>
                        <div className="ml-1 font-semibold text-center text-p-text text-md">
                          {item.label}
                        </div>
                        <button
                          onClick={() => handleIntegrateButtonClick(item)}
                          className="p-2 ml-4 text-xs font-bold transition-all duration-300 rounded-full bg-zinc-100 hover:bg-zinc-300 text-zinc-700"
                        >
                          <MdOutlineAdd size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* ===================== CUSTOM MCP TAB ===================== */}
                {activeTab === "Custom MCP" && (
                  <McpConnectionManager />
                )}
              </>
            )}
          </div>

          {isModalOpen && (
            <>
              {modalAppName === 'jira' ? (
                <JiraResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                />
              ) : modalAppName === 'confluence' ? (
                <ConfluenceResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                />
              ) : modalAppName === "microsoft_teams" ? (
                <TeamsConfigurationModal
                  onClose={closeModal}
                />
              ) : modalAppName === "gohighlevel" ? (
                <GohighlevelResourceModal
                  onClose={closeModal}
                />
              ) : modalAppName === "shiprocket" ? (
                <ShiprocketAuthModal
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onSuccess={() => {
                    closeModal();
                    fetchIntegratedApps();
                  }}
                />
              ) : modalAppName === "razorpay" ? (
                <RazorpayAuthModal
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onSuccess={() => {
                    closeModal();
                    fetchIntegratedApps();
                  }}
                />
              ) : (
                <ResourceModal
                  appName={modalAppName}
                  userGoogleId={user_google_id}
                  onClose={closeModal}
                  onlyEmailApps={onlyEmailApps}
                />
              )}
            </>
          )}
          {/* NEW: Render the MCP Connection Manager inside your Modal component */}

          <Modal
            isOpen={isMcpModalOpen}
            onClose={closeMcpModal}
            title="MCP Connection Manager"
            desc="One-Click Integration with Publicly Accessible MCP Application Servers"
          >
            <McpConnectionManager />
          </Modal>

          {/* QuickBooks Environment Selection Modal */}
          <QuickBooksEnvModal
            isOpen={isQuickBooksEnvModalOpen}
            onClose={() => setIsQuickBooksEnvModalOpen(false)}
            onSelect={handleQuickBooksEnvSelect}
          />
        </div>
      )}
    </div>
  );
};

export default Integration;